/**
 * 날씨 — 위치는 «주소에서 셈한 값»이다 (2026-09-21 · 사용자 결정 「확정으로 할 필요가 없다」).
 *
 * 관리자가 위치를 확정하는 단계가 없다. 서버가 조회할 때마다 회사·사업장 주소에서 격자를 셈해 준다.
 * 그래서 이 모듈은 **읽기만** 한다 — 저장하는 길이 없다.
 */
import { apiClient } from './client';

/** 주소가 풀린 결과. `ok` 가 아니면 날씨를 보여주지 않는다. */
export type WeatherLocation =
  | { kind: 'ok'; nx: number; ny: number; level: 'dong' | 'sigungu'; regionName: string }
  | { kind: 'no_address'; message?: string }
  /** 주소는 있는데 동·시군구까지 못 맞췄다. `level` 은 거기까지라도 맞춘 것(시도)이 있으면 온다. */
  | { kind: 'unresolved'; level: 'sido' | null; regionName: string | null; message?: string };

export interface WeatherPlaceView {
  address: string | null;
  location: WeatherLocation;
  /** 주소를 고칠 수 있는 사람인가. */
  canEditAddress: boolean;
}

export interface WeatherWorksiteView extends WeatherPlaceView {
  id: string;
  name: string;
  isHeadOffice: boolean;
  isActive: boolean;
}

export interface WeatherGridSettings {
  company: WeatherPlaceView;
  /** 사업장 목록을 읽을 권한이 없으면 null — 「없다」가 아니라 「못 본다」. */
  worksites: WeatherWorksiteView[] | null;
}

export async function getWeatherGridSettings(): Promise<WeatherGridSettings> {
  const { data } = await apiClient.get<WeatherGridSettings>('/weather/grid-settings');
  return data;
}

/** 날씨를 보여줄 곳 — 회사(기본) 또는 사업장 하나. */
export interface WeatherPlace {
  kind: 'company' | 'worksite';
  id: string;
  name: string;
}

/** 종류 하나의 결과 — 기상청 범주 코드가 그대로 온다(사람이 읽는 값으로 바꾸는 곳은 lib/weather-view.ts). */
export interface WeatherKindResult {
  kind: string;
  status: 'cached' | 'fresh' | 'stale' | 'unavailable';
  baseAt: string | null;
  reason: string | null;
  summary: { now?: Record<string, string>; series?: Array<{ at: string; values: Record<string, string> }> } | null;
}

export interface WeatherByPlace {
  place: WeatherPlace;
  location: WeatherLocation;
  /**
   * 위치를 못 정했으면 null. 값을 못 가져온 것(공급자 실패 · 공유 한도)은 `usable:false` 로 여기 안에 온다 —
   * ★ 위치 갈래와 **다른 칸**이다. 섞으면 화면이 엉뚱한 안내를 한다.
   */
  weather: {
    usable: boolean;
    reason: string | null;
    providerKey: string | null;
    grid: { nx: number; ny: number };
    results: WeatherKindResult[];
  } | null;
}

/**
 * 회사 · 사업장 주소 기준 날씨.
 * ★ `kinds` 를 좁히면 공급자 호출도 준다 — 시간별 예보를 끈 위젯은 실황·초단기만 부른다.
 */
export async function getWeatherByPlace(input: { worksiteId?: string; place?: 'company'; kinds?: readonly string[] } = {}): Promise<WeatherByPlace> {
  const params: Record<string, string> = {};
  if (input.worksiteId) params.worksiteId = input.worksiteId;
  // 아무것도 안 주면 서버가 «내 사업장, 없으면 회사»로 정한다(화면은 내 근무지를 모른다).
  if (input.place) params.place = input.place;
  if (input.kinds?.length) params.kinds = input.kinds.join(',');
  const { data } = await apiClient.get<WeatherByPlace>('/weather/by-place', { params });
  return data;
}
